import { Box, Flex } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { Modal } from '@kula-ai/components';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useIsMobile } from '@/hooks/use-IsMobile';

type PreviewFile =
  | File
  | {
      url?: string;
      file_name?: string;
      filename?: string;
      name?: string;
      content_type?: string;
      type?: string;
      preview?: { url?: string } | null;
    };

interface PreviewPdfModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  file?: PreviewFile;
}

const getDisplayName = (file?: PreviewFile): string | undefined => {
  if (!file) return undefined;
  if (file instanceof File) return file.name;
  return file.file_name || file.filename || file.name;
};

const getContentType = (file?: PreviewFile): string => {
  if (!file) return '';
  if (file instanceof File) return file.type;
  return file.content_type || file.type || '';
};

const getPreviewUrl = (file?: PreviewFile): string => {
  if (!file || file instanceof File) return '';
  return file.preview?.url ?? '';
};

export const PreviewPdfModal = ({
  isOpen = false,
  onClose = () => {},
  file,
}: PreviewPdfModalProps) => {
  const isMobile = useIsMobile();
  const displayName = getDisplayName(file);

  // Long filenames overflow the header — truncate with a hover tooltip for the full name.
  const titleNode = displayName ? (
    <Box maxW={{ base: '75vw', md: '640px' }} minW={0}>
      <TruncatedTextWithTooltip
        text={displayName}
        placement="bottom"
        textProps={{ size: 'md-semi-bold', color: 'secondary-900' }}
      />
    </Box>
  ) : undefined;

  // Create the blob URL once per File (not on every render) so DocxViewer/PDF viewer
  // don't refetch on each re-render and hang loading. Revoke it on change/unmount.
  const fileUrl = useMemo(() => {
    if (!file) return '';
    if (file instanceof File) return URL.createObjectURL(file);
    return file.url ?? '';
  }, [file]);

  useEffect(() => {
    if (file instanceof File && fileUrl) {
      return () => URL.revokeObjectURL(fileUrl);
    }
  }, [file, fileUrl]);

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={titleNode}
      size={isMobile ? 'full' : 'xl'}
      preventClose={false}
      primaryButton={{ hidden: true }}
      secondaryButton={{ hidden: true }}
    >
      <Flex w="100%" h="70vh" overflow="auto" flexDir="column">
        <PDFReaderBrowser
          file={fileUrl}
          content_type={getContentType(file)}
          preview_url={getPreviewUrl(file)}
          fileName={displayName}
        />
      </Flex>
    </Modal>
  );
};
